// Listing modes for Flights — List / Today / Calendar / Timeline.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// slavishly from src/bookings/modes.ts, itself mirrored from
// src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/list     → FlightsList     (the existing AeronetDataGrid register)
//   <base>/calendar → FlightsCalendar (the scheduler surface over the same rows)
//   <base>/timeline → FlightsCalendar (aircraft rows over the same records)
//
// WHY FLIGHTS IS NOW A **MAJOR** MODULE. PATTERNS.md §"Module weight" says a
// minor module is list + CRUD only and warns "never invent a second view to
// justify a switcher" (blank/BlankLayout.tsx:18-21). That guard is about
// INVENTED views. The Calendar is not invented: Aaron asked for it directly
// (2026-08-31, *"add a mode switcher to flights and add a calendar view same
// as bookings but looking at flight data"*), and it is backed by real columns
// on real rows — `date` on 38/38 active top-level flights and `dep_time` on
// 34/38, measured on module_g2_demo 2026-08-31. So there are two genuine
// SURFACES here, exactly as on Bookings, and the switcher has something real
// to switch between.
import { CALENDAR_VIEWS, isCalendarView, isViewInMode } from "./calendar";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 *
 * "List", not "Register"/"Flights"/"Grid" — 16 of 17 converted modules label
 * their grid mode "List" and a bespoke label is a conformance finding.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  today: "Today",
  calendar: "Calendar",
  timeline: "Timeline",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Every flight",
  today: "The day's flight reports",
  calendar: "Day and week views",
  timeline: "Flights by aircraft",
};

/**
 * List, NOT Calendar — and this differs from Bookings deliberately.
 *
 * Bookings defaults to its scheduler because legacy config
 * `flight_booking_default_mode` is `new_display` on every tenant measured.
 * Flights has NO equivalent legacy config: there is no legacy flight calendar
 * at all (see calendar.ts §"NO LEGACY COUNTERPART"), so nothing says an
 * operator opening /app/flights expects a scheduler. The register is the
 * surface this module has always opened on, and changing that under every
 * existing operator to justify a new view would be a product decision nobody
 * asked for. The remembered mode still wins on the second visit.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * The module's route slug: its `modules.mod_function` (the same convention as
 * flight-duty/modes.ts's FLIGHT_DUTY_SLUG, and the `/flights` the Pilots
 * section's destination list links to). For a surface rendered OUTSIDE this
 * module's routes — PilotsToday on the Pilots home — that has no live module
 * base to derive from. Inside the module, prefer the base FlightsLayout
 * declares from the live pathname.
 */
export const FLIGHTS_SLUG = "flights";

/** `<module>.viewMode`, matching every other converted module's key shape. */
export const MODE_STORAGE_KEY = "flights.viewMode";

/**
 * `<module>.calendarView` — which of the calendar's OWN views was last shown.
 * Separate key from MODE_STORAGE_KEY: the mode is a route, the view is not.
 */
export const CALENDAR_VIEW_STORAGE_KEY = "flights.calendarView";

export const calendarViewStorageKey = (mode: string): string =>
  `${CALENDAR_VIEW_STORAGE_KEY}.${mode}`;

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: Flights never shipped a chip+menu switcher (it
 * had no second view to switch to), so there is no pre-existing stored value
 * in another shape to translate.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Last calendar view, validated the same hasOwnProperty way as the mode —
 * a persisted "__proto__" here would index CALENDAR_VIEWS to a function and
 * crash the renderer rather than 404 it, which is worse, not better.
 */
export function readRememberedCalendarView(fallback: string, mode?: string): string {
  if (typeof window === "undefined") return fallback;
  if (mode) {
    const scoped = window.localStorage.getItem(calendarViewStorageKey(mode));
    if (scoped && isViewInMode(scoped, mode)) return scoped;
    const legacy = window.localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return legacy && isViewInMode(legacy, mode) ? legacy : fallback;
  }
  const stored = window.localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
  return stored && isCalendarView(stored) ? stored : fallback;
}

/** Present only so an unused-import lint cannot hide a CALENDAR_VIEWS rename. */
export const CALENDAR_VIEW_COUNT = Object.keys(CALENDAR_VIEWS).length;

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
